import React, { useState, useEffect } from 'react';
import { getModels } from '../../services/modelService';
import { CostEstimator } from './CostEstimator';
import { Spinner } from '../common/Spinner';

interface ModelComparisonProps {
  inputText?: string;
  messages?: any[];
  darkMode?: boolean;
  className?: string;
}

export const ModelComparison: React.FC<ModelComparisonProps> = ({
  inputText,
  messages,
  darkMode = false,
  className = ''
}) => {
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [estimatedOutputLength, setEstimatedOutputLength] = useState<number>(500);
  const [providerFilter, setProviderFilter] = useState<string>('all');
  
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        const modelsData = await getModels();
        setModels(modelsData);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch models');
      } finally {
        setLoading(false);
      }
    };
    
    fetchModels();
  }, []);
  
  const toggleModelSelection = (modelId: string) => {
    if (selectedModels.includes(modelId)) {
      setSelectedModels(selectedModels.filter(id => id !== modelId));
    } else {
      setSelectedModels([...selectedModels, modelId]);
    }
  };
  
  // Get unique providers
  const providers = loading ? [] : ['all', ...new Set(models.map(m => m.provider))];
  
  // Filter models by provider
  const filteredModels = loading ? [] : (
    providerFilter === 'all' 
      ? models 
      : models.filter(m => m.provider === providerFilter)
  );
  
  if (loading) return <div className="flex justify-center p-4"><Spinner size="md" /></div>;
  if (error) return <div className="text-red-500 p-4">Error loading models: {error}</div>;
  
  return (
    <div className={`${className} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <h2 className="text-xl font-bold mb-4">Model Comparison</h2>
      
      {/* Cost Estimates Section - Fixed height, horizontally scrollable */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">Cost Estimates</h3>
        <div className="h-[420px] relative">
          {selectedModels.length > 0 ? (
            <div className="absolute inset-0 overflow-x-auto overflow-y-hidden pb-2">
              <div className="flex space-x-4" style={{ minWidth: 'max-content' }}>
                {selectedModels.map(modelId => (
                  <div 
                    key={modelId}
                    className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border'}`}
                    style={{ width: '350px', height: '400px' }}
                  >
                    <h3 className="font-medium mb-2">
                      {models.find(m => m.id === modelId)?.name} ({models.find(m => m.id === modelId)?.provider})
                    </h3>
                    <CostEstimator
                      modelId={modelId}
                      inputText={inputText}
                      messages={messages}
                      estimatedOutputLength={estimatedOutputLength}
                      darkMode={darkMode}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`h-full flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p>Select one or more models below to see cost estimates</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mb-6 space-y-4">
        <div>
          <label className="block mb-2">Provider Filter</label>
          <div className="flex flex-wrap gap-2">
            {providers.map(provider => (
              <button
                key={provider}
                onClick={() => setProviderFilter(provider)}
                className={`px-3 py-1 rounded-full text-sm ${
                  providerFilter === provider
                    ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                    : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                }`}
              >
                {provider}
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block mb-2">Estimated Output Length (tokens)</label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="100"
              max="2000"
              step="100"
              value={estimatedOutputLength}
              onChange={(e) => setEstimatedOutputLength(parseInt(e.target.value))}
              className="w-full"
            />
            <span className="w-20 text-right">{estimatedOutputLength}</span>
          </div>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-semibold mb-3">Select Models to Compare</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredModels.map(model => (
            <div 
              key={model.id}
              className={`p-3 rounded-md cursor-pointer transition-colors border ${
                selectedModels.includes(model.id) 
                  ? (darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200') 
                  : (darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100')
              }`}
              onClick={() => toggleModelSelection(model.id)}
            >
              <div className="flex justify-between items-center">
                <div className="font-medium">{model.name}</div>
                <div className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                  {model.provider}
                </div>
              </div>
              
              <div className="mt-2 text-sm">
                <div>Context: {model.context_length.toLocaleString()} tokens</div>
                <div>Input: ${model.prompt_cost_dollars.toFixed(4)}/1K tokens</div>
                <div>Output: ${model.completion_cost_dollars.toFixed(4)}/1K tokens</div>
              </div>
              
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(model.capabilities).map(([key, value]) => (
                  value && (
                    <span 
                      key={key}
                      className={`text-xs px-2 py-0.5 rounded ${
                        darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {key}
                    </span>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
